/**
 * PD-17630: the Workd-wide trade show and association directory
 * (`workd_mission_control.ts_*`).
 *
 * The field lists below are the one definition of what a directory record
 * holds, and the config-web edit panel is built from them. The directory job on
 * the licensing Mac Studio keeps a mirror
 * (scripts/mission-control/directory-job/lib/fields.mjs) whose test fails if the
 * two drift apart. `column` is the database column name.
 */

export type DirectoryRecordType = 'show' | 'association';

export type DirectoryFieldType =
	| 'text'
	| 'longText'
	| 'date'
	| 'money'
	| 'count'
	| 'boolean'
	| 'url'
	| 'list';

export type DirectoryFieldGroup =
	| 'show'
	| 'prices'
	| 'rules'
	| 'deadlines'
	| 'association'
	| 'dues';

export interface DirectoryFieldModel {
	readonly column: string;
	readonly label: string;
	readonly type: DirectoryFieldType;
	readonly group: DirectoryFieldGroup;
	/** Set by an admin only; the daily job never reads this from a page. */
	readonly adminOnly?: boolean;
}

export const DIRECTORY_SHOW_FIELDS: ReadonlyArray<DirectoryFieldModel> = [
	{ column: 'name', label: 'Name', type: 'text', group: 'show', adminOnly: true },
	{ column: 'organizer', label: 'Organizer', type: 'text', group: 'show', adminOnly: true },
	{ column: 'website_url', label: 'Website', type: 'url', group: 'show', adminOnly: true },
	{ column: 'starts_on', label: 'Starts', type: 'date', group: 'show' },
	{ column: 'ends_on', label: 'Ends', type: 'date', group: 'show' },
	{ column: 'venue', label: 'Venue', type: 'text', group: 'show' },
	{ column: 'venue_address', label: 'Venue address', type: 'text', group: 'show' },
	{ column: 'city', label: 'City', type: 'text', group: 'show' },
	{ column: 'state', label: 'State', type: 'text', group: 'show' },
	{ column: 'sectors', label: 'Sectors', type: 'list', group: 'show', adminOnly: true },
	{ column: 'registration_url', label: 'Registration page', type: 'url', group: 'show' },
	{ column: 'exhibitor_url', label: 'Exhibitor page', type: 'url', group: 'show' },
	{ column: 'hotel_block_url', label: 'Hotel block page', type: 'url', group: 'show' },
	{ column: 'cost_exhibit_member', label: 'Booth 10x10, member', type: 'money', group: 'prices' },
	{ column: 'cost_exhibit_nonmember', label: 'Booth 10x10, non-member', type: 'money', group: 'prices' },
	{ column: 'exhibit_includes_people', label: 'Badges included with a booth', type: 'count', group: 'prices' },
	{ column: 'cost_extra_person_exhibit', label: 'Extra exhibitor badge', type: 'money', group: 'prices' },
	{ column: 'cost_attend_vendor', label: 'Vendor attend price', type: 'money', group: 'prices' },
	{ column: 'cost_attend_vendor_people', label: 'People that price covers', type: 'count', group: 'prices' },
	{ column: 'cost_sponsorship_entry', label: 'Lowest sponsorship', type: 'money', group: 'prices' },
	{ column: 'hotel_block_rate', label: 'Hotel block rate', type: 'money', group: 'prices' },
	{ column: 'cost_notes', label: 'Price notes', type: 'longText', group: 'prices' },
	{ column: 'cost_as_of', label: 'Prices as of', type: 'text', group: 'prices' },
	{ column: 'membership_required_for', label: 'Membership required', type: 'text', group: 'rules' },
	{ column: 'membership_annual', label: 'Membership dues', type: 'money', group: 'rules' },
	{ column: 'membership_one_time', label: 'Membership one-time fee', type: 'money', group: 'rules' },
	{ column: 'vendor_can_attend', label: 'Vendors can attend', type: 'boolean', group: 'rules' },
	{ column: 'vendor_attend_requires_booth', label: 'Vendors must exhibit to attend', type: 'boolean', group: 'rules' },
	{ column: 'vendor_attend_note', label: 'How vendors attend', type: 'text', group: 'rules' },
	{ column: 'extra_badges_note', label: 'Extra badges', type: 'text', group: 'rules' },
	{ column: 'attendee_list_policy', label: 'Attendee list', type: 'longText', group: 'rules' },
	{ column: 'outside_events_policy', label: 'Outside events', type: 'longText', group: 'rules' },
	{ column: 'early_bird_deadline', label: 'Early bird ends', type: 'date', group: 'deadlines' },
	{ column: 'registration_deadline', label: 'Registration closes', type: 'date', group: 'deadlines' },
	{ column: 'booth_deadline', label: 'Booth deadline', type: 'date', group: 'deadlines' },
	{ column: 'hotel_block_deadline', label: 'Hotel block closes', type: 'date', group: 'deadlines' },
	{ column: 'booth_materials_deadline', label: 'Booth graphics ship by', type: 'date', group: 'deadlines' },
	{ column: 'advance_warehouse_opens', label: 'Advance warehouse opens', type: 'date', group: 'deadlines' },
	{ column: 'attendee_list_sent', label: 'Attendee list sent', type: 'date', group: 'deadlines' },
];

export const DIRECTORY_ASSOCIATION_FIELDS: ReadonlyArray<DirectoryFieldModel> = [
	{ column: 'name', label: 'Name', type: 'text', group: 'association', adminOnly: true },
	{ column: 'short_name', label: 'Short name', type: 'text', group: 'association', adminOnly: true },
	{ column: 'website_url', label: 'Website', type: 'url', group: 'association', adminOnly: true },
	{ column: 'association_type', label: 'Type', type: 'text', group: 'association', adminOnly: true },
	{ column: 'industries', label: 'Sectors', type: 'list', group: 'association', adminOnly: true },
	{ column: 'summary', label: 'Summary', type: 'longText', group: 'association', adminOnly: true },
	{ column: 'dues_annual', label: 'Annual dues', type: 'money', group: 'dues' },
	{ column: 'dues_application', label: 'Application fee', type: 'money', group: 'dues' },
	{ column: 'dues_category', label: 'Dues category', type: 'text', group: 'dues' },
	{ column: 'dues_notes', label: 'Dues notes', type: 'longText', group: 'dues' },
	{ column: 'dues_as_of', label: 'Dues as of', type: 'text', group: 'dues' },
	{ column: 'unlocks', label: 'What membership unlocks', type: 'longText', group: 'dues' },
];

export const directoryFields = (
	recordType: DirectoryRecordType,
): ReadonlyArray<DirectoryFieldModel> =>
	recordType === 'show' ? DIRECTORY_SHOW_FIELDS : DIRECTORY_ASSOCIATION_FIELDS;

const CORRECTABLE_SHOW_COLUMNS: ReadonlyArray<string> = ['starts_on', 'ends_on', 'venue', 'city', 'state'];

/** PD-17632: what a tenant may send Workd a correction for: dates, venue, deadlines. */
export const DIRECTORY_CORRECTION_FIELDS: ReadonlyArray<DirectoryFieldModel> = DIRECTORY_SHOW_FIELDS.filter(
	(field) => field.group === 'deadlines' || CORRECTABLE_SHOW_COLUMNS.includes(field.column),
);

/** Change-log entries that aren't one field: a source page, or the record itself. */
const DIRECTORY_OTHER_LABELS: Readonly<Record<string, string>> = {
	overview: 'Website',
	prices: 'Prices',
	dues: 'Dues',
	schedule: 'Schedule',
	exhibitors: 'Exhibitors',
	members: 'Members',
	dates: 'Dates',
	edition: 'New edition',
	page: 'Page',
	record: 'Record',
	correction: 'Correction',
};

export const directoryFieldLabel = (
	recordType: DirectoryRecordType,
	column: string,
): string =>
	directoryFields(recordType).find((field) => field.column === column)?.label ??
	DIRECTORY_OTHER_LABELS[column] ??
	column;

export const DIRECTORY_SCHEDULE_KINDS = [
	'registration',
	'move_in',
	'final_set',
	'show_hours',
	'move_out',
	'general_session',
	'education',
	'social',
	'meal',
	'meeting',
	'add_on',
	'other',
] as const;

export type DirectoryScheduleKind = (typeof DIRECTORY_SCHEDULE_KINDS)[number];

export const DIRECTORY_SCHEDULE_AUDIENCES = [
	'everyone',
	'exhibitors',
	'distributors',
	'members',
	'invitation',
] as const;

export type DirectoryScheduleAudience =
	(typeof DIRECTORY_SCHEDULE_AUDIENCES)[number];

/** Where a directory change came from. */
export type DirectoryChangeSource = 'daily_job' | 'tenant_correction' | 'admin';

/** Applied, or left unchanged and put in the run's email. */
export type DirectoryChangeResult = 'applied' | 'emailed';

/** The status column on the Directory lists. */
export type DirectoryRecordStatus = 'current' | 'updated' | 'cant_check' | 'hidden';

/** A change within this many days reads "Updated <date>" rather than "Current". */
export const DIRECTORY_UPDATED_WINDOW_DAYS = 7;

/** One message per run, with what automation could not decide. */
export const DIRECTORY_NOTIFICATION_EMAIL = 'notifications@workd.com';
